import React, { useState, useEffect, useRef } from 'react';
import { App, Input, Button, Segmented, Select, Slider, ColorPicker, Modal, Popconfirm } from 'antd';
import { UploadOutlined, CheckOutlined, LoadingOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api, { errorCode } from './api';
import AvatarComponent from '../components/AvatarComponent';
import { avatarSrc } from '../components/ProfileView';
import { getProfileStyle, DEFAULT_DESIGN, FONT_FAMILY_MAP } from '../utils/profileStyle';
import { TEMPLATES } from '../utils/templates';
import resizeImage from '../utils/resizeImage';
import { BIO_MAX_LENGTH, NAME_MAX_LENGTH, enums } from '../config';

// A text field that saves itself when it loses focus, and says so
const AutoSaveField = ({ label, value, maxLength, multiline, placeholder, onSave }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [draft, setDraft] = useState(value || '');
  const [state, setState] = useState('idle');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    if (draft.trim() === (value || '')) return;
    setState('saving');
    onSave(draft.trim())
      .then(() => { setState('saved'); setTimeout(() => setState('idle'), 1500); })
      // The text stays in the field, marked unsaved; leaving the field
      // again retries, since it still differs from the saved value
      .catch(() => { setState('failed'); message.error(t('dash.saveFailed')); });
  };

  const Field = multiline ? Input.TextArea : Input;
  return (
    <label className="dash-field">
      <span className="dash-field__label">
        {label}
        <em className="dash-field__meta">
          {state === 'saving' ? t('dash.saving')
            : state === 'saved' ? <><CheckOutlined /> {t('saved')}</>
            : state === 'failed' && draft.trim() !== (value || '') ? <span className="dash-field__meta--failed">{t('dash.notSaved')}</span>
            : `${draft.length}/${maxLength}`}
        </em>
      </span>
      <Field value={draft} maxLength={maxLength} placeholder={placeholder} onChange={e => setDraft(e.target.value)} onBlur={commit}
        {...(multiline ? { autoSize: { minRows: 2, maxRows: 5 } } : { onPressEnter: e => e.target.blur() })} />
    </label>
  );
};

// Label above, control below, like every other field on this panel; the
// trigger stretches to the column so it lines up with inputs and selects
const ColorField = ({ label, value, onPreview, onSave }) => (
  <div className="dash-field dash-color">
    <span>{label}</span>
    <ColorPicker value={value} disabledAlpha showText format="hex" aria-label={label}
      onChange={color => onPreview(color.toHexString())}
      onChangeComplete={color => onSave(color.toHexString())} />
  </div>
);

const Section = ({ title, children }) => (
  <section className="dash-card dash-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const DesignPanel = ({ user, setUser, save }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [pickingImage, setPickingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Design changes save on their own; this says so, like the text fields do
  const [saveState, setSaveState] = useState('idle');
  const savedTimer = useRef(null);
  useEffect(() => () => clearTimeout(savedTimer.current), []);
  const fileInput = useRef(null);
  const design = { ...DEFAULT_DESIGN, ...user.design };
  const username = user.username;

  // Colour drags repaint the preview immediately and save once released
  const preview = (fields) => setUser({ ...user, design: { ...design, ...fields } });
  const track = (promise) => {
    clearTimeout(savedTimer.current);
    setSaveState('saving');
    return promise
      .then(result => {
        setSaveState('saved');
        savedTimer.current = setTimeout(() => setSaveState('idle'), 1800);
        return result;
      }, err => {
        // save() has already put the last confirmed design back on screen
        setSaveState('failed');
        savedTimer.current = setTimeout(() => setSaveState('idle'), 3000);
        throw err;
      });
  };
  const update = (fields) => {
    preview(fields);
    return track(save(api.design(username, fields))).catch(() => message.error(t('dash.saveFailed')));
  };

  const changeBackground = (value) => {
    // 'image' can be picked before a photo exists; the server only switches
    // to it once an upload succeeds
    if (value === 'image' && !design.backgroundImage) return setPickingImage(true);
    setPickingImage(false);
    update({ backgroundType: value });
  };

  const uploadBackground = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    resizeImage(file)
      .then(blob => {
        const form = new FormData();
        form.append('background', blob, 'background.jpg');
        return track(save(api.background(username, form)));
      })
      .then(() => setPickingImage(false))
      .catch(err => message.warning(errorCode(err) === enums.errorCodes.c15 ? t('design.imageInvalid') : t('design.imageUploadFailed')))
      .finally(() => setUploading(false));
  };

  const hasPhoto = !!user.avatar && user.avatar !== 'uploads/default.png';
  const backgroundType = pickingImage ? 'image' : design.backgroundType;
  const activeTemplate = TEMPLATES.find(tpl => Object.keys(tpl.design).every(key => String(design[key]).toLowerCase() === String(tpl.design[key]).toLowerCase()));

  return (
    <div className="dash-panel">
      <header className="dash-panel__head">
        <h1>{t('dash.nav.design')}</h1>
        <span className={`dash-save-state is-${saveState}`} role="status" aria-live="polite">
          {saveState === 'saving' && <><LoadingOutlined /> {t('dash.saving')}</>}
          {saveState === 'saved' && <><CheckOutlined /> {t('saved')}</>}
          {saveState === 'failed' && <><CloseCircleOutlined /> {t('dash.notSaved')}</>}
        </span>
      </header>

      <Section title={t('dash.profile')}>
        <div className="dash-profile">
          <button type="button" className="dash-avatar" onClick={() => setAvatarOpen(true)} aria-label={t('dash.changePhoto')}>
            <img src={avatarSrc(user.avatar)} alt="" />
          </button>
          <div className="dash-profile__actions">
            <Button onClick={() => setAvatarOpen(true)}>{t(hasPhoto ? 'dash.changePhoto' : 'dash.addPhoto')}</Button>
            {hasPhoto &&
              <Popconfirm title={t('dash.removePhotoConfirm')} okText={t('dash.removePhoto')} cancelText={t('dash.cancel')} okButtonProps={{ danger: true }}
                onConfirm={() => save(api.defaultAvatar(username)).catch(() => message.error(t('dash.saveFailed')))}>
                <Button type="text">{t('dash.removePhoto')}</Button>
              </Popconfirm>}
          </div>
        </div>
        <AutoSaveField label={t('title')} value={user.name} maxLength={NAME_MAX_LENGTH} placeholder={t('titlePlaceholder')}
          onSave={name => save(api.name(username, name))} />
        <AutoSaveField label={t('bio')} value={user.bio} maxLength={BIO_MAX_LENGTH} placeholder={t('bioPlaceholder')} multiline
          onSave={bio => save(api.bio(username, bio))} />
        <Modal open={avatarOpen} onCancel={() => setAvatarOpen(false)} footer={null} centered width={360} destroyOnClose title={t(hasPhoto ? 'dash.changePhoto' : 'dash.addPhoto')}>
          <AvatarComponent username={username} avatar={avatarSrc(user.avatar)} hasPhoto={hasPhoto} closeModal={() => setAvatarOpen(false)}
            rerender={() => api.me().then(setUser)} onError={text => message.error(text || t('dash.photoFailed'))} />
        </Modal>
      </Section>

      <Section title={t('dash.templates')}>
        <div className="dash-templates">
          {TEMPLATES.map(tpl => {
            const { style, className } = getProfileStyle(tpl.design);
            return (
              <button key={tpl.id} type="button" className={`dash-template ${activeTemplate === tpl ? 'is-active' : ''}`} aria-pressed={activeTemplate === tpl} onClick={() => update(tpl.design)}>
                <span className={`dash-template__swatch ${className}`} style={style}>
                  <i className="link" /><i className="link" />
                </span>
                <span className="dash-template__name">{activeTemplate === tpl && <CheckOutlined />}{t(`dash.templateNames.${tpl.id}`)}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section title={t('dash.background')}>
        <Segmented block value={backgroundType} onChange={changeBackground}
          options={[
            { value: 'solid', label: t('design.solid') },
            { value: 'gradient', label: t('design.gradient') },
            { value: 'image', label: t('design.image') }
          ]} />
        <div className="dash-grid">
          {backgroundType === 'solid' &&
            <ColorField label={t('design.backgroundColor')} value={design.backgroundColor} onPreview={v => preview({ backgroundColor: v })} onSave={v => update({ backgroundColor: v })} />}
          {backgroundType === 'gradient' && <>
            <ColorField label={t('design.gradientFrom')} value={design.gradientFrom} onPreview={v => preview({ gradientFrom: v })} onSave={v => update({ gradientFrom: v })} />
            <ColorField label={t('design.gradientTo')} value={design.gradientTo} onPreview={v => preview({ gradientTo: v })} onSave={v => update({ gradientTo: v })} />
          </>}
        </div>
        {backgroundType === 'image' &&
          <div className="dash-bgimage">
            <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadBackground} hidden />
            {design.backgroundImage && <img className="dash-bgimage__thumb" src={'/' + design.backgroundImage} alt="" />}
            <div className="dash-bgimage__controls">
              <Button icon={<UploadOutlined />} loading={uploading} onClick={() => fileInput.current.click()}>
                {design.backgroundImage ? t('design.changeImage') : t('design.uploadImage')}
              </Button>
              {design.backgroundImage &&
                <label className="dash-field">
                  <span>{t('design.overlay')}</span>
                  <Slider min={0} max={80} step={5} value={design.backgroundOverlay}
                    onChange={value => preview({ backgroundOverlay: value })}
                    onChangeComplete={value => update({ backgroundOverlay: value })} />
                </label>}
            </div>
          </div>}
      </Section>

      <Section title={t('dash.buttons')}>
        <div className="dash-grid dash-grid--2-3">
          <div className="dash-field">
            <span>{t('design.buttonStyle')}</span>
            <Segmented block value={design.buttonStyle} onChange={value => update({ buttonStyle: value })}
              options={[{ value: 'filled', label: t('design.filled') }, { value: 'outline', label: t('design.outline') }]} />
          </div>
          <div className="dash-field">
            <span>{t('design.buttonShape')}</span>
            <Segmented block value={design.buttonShape} onChange={value => update({ buttonShape: value })}
              options={[{ value: 'square', label: t('design.square') }, { value: 'rounded', label: t('design.rounded') }, { value: 'pill', label: t('design.pill') }]} />
          </div>
        </div>
        <div className="dash-grid">
          <ColorField label={t('design.buttonColor')} value={design.buttonColor} onPreview={v => preview({ buttonColor: v })} onSave={v => update({ buttonColor: v })} />
          <ColorField label={t('design.buttonTextColor')} value={design.buttonTextColor} onPreview={v => preview({ buttonTextColor: v })} onSave={v => update({ buttonTextColor: v })} />
        </div>
      </Section>

      <Section title={t('dash.text')}>
        <div className="dash-grid">
          <label className="dash-field">
            <span>{t('design.font')}</span>
            <Select value={design.font} onChange={value => update({ font: value })}
              // Each font shows in its own face in the list only; the chosen
            // value sits in the field at the same size as every other input
            options={Object.keys(FONT_FAMILY_MAP).map(font => ({ value: font, label: t(`design.fonts.${font}`) }))}
            optionRender={option => <span style={{ fontFamily: FONT_FAMILY_MAP[option.value] }}>{option.label}</span>} />
          </label>
          <ColorField label={t('design.textColor')} value={design.textColor} onPreview={v => preview({ textColor: v })} onSave={v => update({ textColor: v })} />
        </div>
      </Section>
    </div>
  );
};

export default DesignPanel;
